/**
 * Container for the Add Annual Salary modal (SE-8409).
 *
 * Self-contained: unlike Sell/Loan/Promote/Sign (which bubble raw values up
 * to `ActionMenuContainer` for scenario draft-staging via `addDraftAction`),
 * a manual annual salary is a direct, org-level write — there is no draft to
 * stage. So this container owns the mutation call itself, which is what lets
 * every mount point (the scenario ActionMenu, the Current Squad "+ Add
 * action" trigger, and the row's click-to-edit) reuse it with zero duplicated
 * save logic.
 *
 * Resets its draft state by remounting rather than by effect (this repo's
 * `react-hooks/set-state-in-effect` rule) — every call site mounts this
 * behind a `key` that changes with the player/open-state, so a fresh open
 * always starts from a fresh `useState(initialAnnualSalary)`.
 * @module features/squad-planning/components/AddAnnualSalaryModal
 */

import React, { memo, useCallback, useState } from "react";

import { useToastNotification } from "@/hooks/useToastNotification";
import { usePlayerOverridesContext } from "@/features/squad-planning/contexts/PlayerOverridesContext";
import { useActiveScenarioId } from "@/features/squad-planning/stores/scenarioStore";
import {
  CompliancePlanStatusDocument,
  useSquadPlanningUpdatePlayerWageMutation,
} from "@/generated/graphql";

import AddAnnualSalaryModalView from "./AddAnnualSalaryModalView";

/** Props for {@link AddAnnualSalaryModalContainer} */
export interface AddAnnualSalaryModalProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Called after a successful save, or when the user cancels */
  readonly onClose: () => void;
  /** The player whose salary this sets */
  readonly playerId: string;
  /** Player name for the title */
  readonly playerName: string;
  /** The row's current salary, pre-filling the input for edit */
  readonly initialAnnualSalary?: number | null;
}

/**
 * Container managing Add Annual Salary modal state and the save mutation.
 * @param root0 - Component props
 * @param root0.isOpen - Whether the modal is visible
 * @param root0.onClose - Called after a successful save, or on cancel
 * @param root0.playerId - The player whose salary this sets
 * @param root0.playerName - Player name for the title
 * @param root0.initialAnnualSalary - Current salary, pre-filling the input
 */
const AddAnnualSalaryModalContainer = ({
  isOpen,
  onClose,
  playerId,
  playerName,
  initialAnnualSalary = null,
}: AddAnnualSalaryModalProps) => {
  const [annualSalary, setAnnualSalary] = useState<number | null>(
    initialAnnualSalary
  );
  const [isSaving, setIsSaving] = useState(false);
  const { setWage } = usePlayerOverridesContext();
  const toast = useToastNotification();
  const [updatePlayerWage] = useSquadPlanningUpdatePlayerWageMutation();
  // SE-8504: this is the mutation the "+ Add action" → "Add Annual Salary"
  // flow actually calls (every mount point — Current Squad, the scenario
  // ActionMenu, and the row's click-to-edit — shares this one container).
  // Without scenarioId, a scenario-mounted save wrote the shared Current
  // Squad row instead of that scenario's own isolated copy.
  const activeScenarioId = useActiveScenarioId();

  const handleConfirm = useCallback(() => {
    if (annualSalary == null || annualSalary <= 0) return;
    setIsSaving(true);
    updatePlayerWage({
      variables: {
        input: {
          playerId,
          grossAnnualSalary: annualSalary,
          ...(activeScenarioId != null && { scenarioId: activeScenarioId }),
        },
      },
      // SE-8761: the Plan Status tile (financial-compliance's
      // `usePlanStatus`) reads `CompliancePlanStatus` keyed by this same
      // scenario, and nothing was refetching or cache-updating it after this
      // mutation resolved — it only picked up the new ratio on the NEXT
      // unrelated fetch (e.g. a page reload), which read as "the tile waits
      // for a reload". This mutation's own response carries no normalized
      // `ComplianceOverview` entity for Apollo to merge automatically (see
      // `operations.graphql`'s selection set), so a declared refetch is the
      // only way to land the new figure. `awaitRefetchQueries` holds the
      // optimistic override and modal close until the tile has the real
      // number, matching the pattern `objectivesSource.ts`'s
      // `OBJECTIVE_WRITE_REFETCH` already uses for the same tile.
      refetchQueries: [CompliancePlanStatusDocument],
      awaitRefetchQueries: true,
    })
      .then(() => {
        // Optimistic override so the row reflects the new value immediately,
        // the same mechanism EditableMoneyCell already uses for wage edits.
        setWage(playerId, annualSalary);
        setIsSaving(false);
        onClose();
      })
      .catch((err: Error) => {
        setIsSaving(false);
        toast.show({
          action: "danger",
          description: err?.message ?? "Failed to save annual salary",
        });
      });
  }, [
    annualSalary,
    playerId,
    updatePlayerWage,
    setWage,
    onClose,
    toast,
    activeScenarioId,
  ]);

  if (!isOpen) return null;

  return (
    <AddAnnualSalaryModalView
      isOpen={isOpen}
      playerName={playerName}
      annualSalary={annualSalary}
      onAnnualSalaryChange={setAnnualSalary}
      onConfirm={handleConfirm}
      onCancel={onClose}
      isSaving={isSaving}
    />
  );
};

AddAnnualSalaryModalContainer.displayName = "AddAnnualSalaryModalContainer";

export default memo(AddAnnualSalaryModalContainer);
